import { useCallback, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import Input from "../../components/atoms/Input";
import Button from "../../components/atoms/Button";
import { RequestField } from "../../app/types/RequestsTypes";
import TextArea from "../../components/atoms/TextArea";
import DatePicker from "../../components/atoms/DatePicker";
import TimeInput from "../../components/atoms/TimePicker";
import moment from "moment";
import NumberPicker from "../../components/molecules/NumberPicker";
import PhonePicker from "../../components/molecules/PhonePicker";
import { Grid2 } from "@mui/material";
import LoaderGlobal from "../../components/molecules/LoaderGlobal";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { getFontClass } from "../../helpers/fontUtils";

type Props = {
  requestsSettings: RequestField[];
  createRequest: any;
  id_room: string | undefined;
};

const mandatorySuffix = (field: RequestField) =>
  field.status === "STATUS_MANDATORY" ? " *" : "";

const SIMPLE_INPUT_FIELDS: Record<string, { id: string; fallback: string }> = {
  TYPE_DELIVERY_ADDRESS: {
    id: "delivery_address",
    fallback: "Delivery address",
  },
  TYPE_DELIVERY_ZIP: { id: "delivery_zip", fallback: "Delivery zip" },
  TYPE_DELIVERY_CITY: { id: "delivery_city", fallback: "Delivery city" },
  TYPE_ROOM_NUMBER: { id: "room_number", fallback: "Room number" },
  TYPE_COMPANY_ADDRESS: { id: "company_address", fallback: "Company address" },
  TYPE_COMPANY_ORGANIZATION_NUMBER: {
    id: "company_organization_number",
    fallback: "Company organization number",
  },
  TYPE_COMPANY_CITY: { id: "company_city", fallback: "Company city" },
  TYPE_COMPANY_ZIP: { id: "company_zip", fallback: "Company zip" },
  TYPE_COMPANY_PHONE: { id: "company_phone", fallback: "Company phone" },
  TYPE_COMPANY_WEBSITE: { id: "company_website", fallback: "Company website" },
  TYPE_PO_NOTE: { id: "po_note", fallback: "PO note" },
};

const RequestForm = ({ requestsSettings, createRequest, id_room }: Props) => {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FieldValues>({
    defaultValues: {
      date: moment().format("YYYY-MM-DD"),
      birthday: null,
    },
  });
  const { generalSettings, locale } = useSelector(
    (state: RootState) => state.generalSettings,
  );

  const [countryCode, setCountryCode] = useState<number>(47);
  const today = moment().format("YYYY-MM-DD");
  const choosenDate = watch("date", undefined);
  const fontClass = getFontClass(generalSettings.events_styling.font_family);

  const renderList = (list: RequestField | undefined) => {
    if (!list || list.status === "STATUS_HIDDEN") return null;

    return (
      <Grid2 size={list.grid}>
        <Controller
          name="list"
          control={control}
          rules={{
            required: list.status === "STATUS_MANDATORY",
          }}
          render={({ field }) => {
            return (
              <div className={`flex flex-col py-[16px] ${fontClass}`}>
                <label className="mb-4" htmlFor="list">
                  {generalSettings.events_text.listLabel[locale] ||
                    globalThis.translate.em.request.form.fields.list.label}
                  {list.status === "STATUS_MANDATORY" ? " *" : ""}
                </label>
                {list.list_options?.map((option) => {
                  return (
                    <div
                      className="flex flex-row gap-[10px] items-center mb-4 h-6"
                      key={`option-${option.text}`}
                    >
                      <input
                        type="checkbox"
                        className="h-6 w-6"
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          if (list.list_type === "LIST_SINGLE") {
                            if (event.target.checked) {
                              setValue("list", [option.text]);
                            } else {
                              setValue("list", []);
                            }
                          } else {
                            const currentList = watch("list") || [];
                            if (event.target.checked) {
                              setValue("list", [...currentList, option.text]);
                            } else {
                              setValue(
                                "list",
                                currentList.filter(
                                  (el: string) => el !== option.text,
                                ),
                              );
                            }
                          }
                        }}
                        checked={
                          !!watch("list") &&
                          watch("list")?.find(
                            (el: string) => el === option.text,
                          )
                        }
                        name="list"
                      />

                      <label htmlFor={option.text}>{option.text}</label>
                    </div>
                  );
                })}
                {errors.list && (
                  <div className="text-red-600 text-sm cursor-default">
                    {globalThis.translate.em.request.form.fields.list.required}
                  </div>
                )}
              </div>
            );
          }}
        />
      </Grid2>
    );
  };

  const renderField = (field: RequestField) => {
    if (field.status === "STATUS_HIDDEN") return null;

    const fieldLabels = globalThis.translate.em.request.form.fields;
    const isMandatory = field.status === "STATUS_MANDATORY";
    const suffix = mandatorySuffix(field);

    const simpleInputConfig = SIMPLE_INPUT_FIELDS[field.type];
    if (simpleInputConfig) {
      const { id, fallback } = simpleInputConfig;
      return (
        <Input
          id={id}
          restProps={register(id, { required: isMandatory })}
          errors={errors}
          label={`${fieldLabels[id as keyof typeof fieldLabels]?.label || fallback}${suffix}`}
        />
      );
    }

    switch (field.type) {
      case "TYPE_PAX":
        return (
          <NumberPicker
            id="pax"
            label={`${fieldLabels.pax?.label || "Pax"}${suffix}`}
            errors={errors}
            control={control}
            min={1}
            restProps={{
              ...register("pax", {
                required: fieldLabels.pax?.required || false,
                value: 1,
                pattern: {
                  value: /^[0-9]/i,
                  message: fieldLabels.pax?.number || "Invalid number",
                },
              }),
            }}
          />
        );
      case "TYPE_DATE":
        return (
          <DatePicker
            label={`${fieldLabels.date?.label || "Date"}${suffix}`}
            errors={errors}
            control={control}
            id="date"
            disablePast={true}
            restProps={{
              ...register("date", {
                required: fieldLabels.date?.required || false,
                validate: (value) =>
                  moment(value, "YYYY-MM-DD")
                    .startOf("day")
                    .isSameOrAfter(moment().startOf("day")) ||
                  fieldLabels.date?.pastError ||
                  "Date cannot be in the past",
              }),
            }}
          />
        );
      case "TYPE_TIME":
        return (
          <TimeInput
            id="time"
            control={control}
            errors={errors}
            restProps={{
              ...register("time", {
                required: fieldLabels.time?.required || false,
              }),
            }}
            label={`${fieldLabels.time?.label || "Time"}${suffix}`}
            disablePast={
              !!choosenDate &&
              moment(choosenDate).format("YYYY-MM-DD") === today
            }
          />
        );
      case "TYPE_NAME":
        return (
          <Input
            id="name"
            restProps={register("name", {
              required: isMandatory ? fieldLabels.name?.required : false,
            })}
            errors={errors}
            label={`${fieldLabels.name?.label || "Name"}${suffix}`}
          />
        );
      case "TYPE_LAST_NAME":
        return (
          <Input
            id="last_name"
            restProps={register("last_name", {
              required: isMandatory ? fieldLabels.lastname?.required : false,
            })}
            errors={errors}
            label={`${fieldLabels.lastname?.label || "Last Name"}${suffix}`}
          />
        );
      case "TYPE_PHONE":
        return (
          <PhonePicker
            label={`${fieldLabels.phone?.label || "Phone"}${suffix}`}
            id="phone"
            setCountryCode={setCountryCode}
            countryCode={countryCode}
            errors={errors}
            control={control}
            restProps={register("phone", {
              required: isMandatory ? fieldLabels.phone?.required : false,
            })}
          />
        );
      case "TYPE_EMAIL":
        return (
          <Input
            id="email"
            restProps={register("email", {
              required: isMandatory ? fieldLabels.email?.required : false,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: fieldLabels.email?.invalid || "Invalid email",
              },
            })}
            type="email"
            errors={errors}
            label={`${fieldLabels.email?.label || "Email"}${suffix}`}
          />
        );
      case "TYPE_COMPANY":
        return (
          <Input
            id="company"
            restProps={{
              ...register("company", {
                required: isMandatory ? fieldLabels.company?.required : false,
              }),
            }}
            errors={errors}
            label={`${fieldLabels.company?.label || "Company"}${suffix}`}
          />
        );
      case "TYPE_BIRTHDAY_DATE":
        return (
          <DatePicker
            label={`${fieldLabels.birthday?.label || "Birthday"}${suffix}`}
            id="birthday"
            errors={errors}
            control={control}
            disableFuture={true}
            restProps={register("birthday", {
              required: isMandatory ? fieldLabels.birthday?.required : false,
            })}
          />
        );
      case "TYPE_LIST":
        return renderList(field);
      case "TYPE_NOTE":
        return (
          <TextArea
            id="note"
            restProps={register("note", { required: isMandatory })}
            errors={errors}
            label={`${fieldLabels.note?.label || "Note"}${suffix}`}
          />
        );
      case "TEXT":
        return (
          <div
            className={`my-6 text-lg ${fontClass}`}
            dangerouslySetInnerHTML={{
              __html: field.text_options[locale === "nb" ? "no" : locale],
            }}
          />
        );
      case "TYPE_DELIVERY_NOTE":
        return (
          <TextArea
            id="delivery_note"
            restProps={register("delivery_note", { required: isMandatory })}
            errors={errors}
            label={`${fieldLabels.delivery_note?.label || "Delivery note"}${suffix}`}
          />
        );
      case "TYPE_COMPANY_EMAIL":
        return (
          <Input
            id="company_email"
            restProps={register("company_email", {
              required: isMandatory,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: fieldLabels.company_email?.invalid || "Invalid email",
              },
            })}
            type="email"
            errors={errors}
            label={`${fieldLabels.company_email?.label || "Company email"}${suffix}`}
          />
        );
      default:
        return null;
    }
  };

  const onSubmit = useCallback(() => {
    const values = getValues();

    createRequest({
      ...values,
      date:
        values.date !== "" ? moment(values.date).format("YYYY-MM-DD") : null,
      birthday:
        values.birthday !== "" && values.birthday !== null && values?.birthday
          ? moment(values.birthday).format("YYYY-MM-DD")
          : undefined,
      time:
        values.time !== ""
          ? moment(values.time, "HH:mm").format("HH:mm")
          : undefined,
      list: !values.list || values.list.length === 0 ? undefined : values.list,
      phone: values.phone ? `+${countryCode} ${values.phone}` : undefined,
      id_room: !!id_room ? Number(id_room) : undefined,
      lang: locale,
    });
  }, [getValues, createRequest, id_room, countryCode, locale]);

  if (!requestsSettings || !choosenDate) {
    return <LoaderGlobal />;
  }

  return (
    <div
      className={`justify-center flex flex-col p-4 lg:w-[40vw] w-[80vw] mt-4 ${fontClass}`}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <Grid2 container columnSpacing={2}>
          {requestsSettings.map((field, index) => (
            <Grid2 key={`field-${index}`} size={field.grid}>
              {renderField(field)}
            </Grid2>
          ))}
          <Button type="submit" className="p-4 w-full mt-[10px] ">
            {globalThis.translate.em.request.form.submit_button}
          </Button>
          <Link className="w-full" to={`/${generalSettings.url}/em`}>
            <Button type="button" className="p-4 w-full mt-[10px] ">
              {globalThis.translate.em.request.form.back_button}
            </Button>
          </Link>
        </Grid2>
      </form>
    </div>
  );
};

export default RequestForm;
